/**
 * Zod validation schemas for shift forms
 * Provides form validation and transformation to API request format
 */

import { z } from 'zod';
import type { I18n } from '@lingui/core';
import { format } from 'date-fns';
import type { Shift, CreateShiftRequest, UpdateShiftRequest } from '@/types/shifts';
import { ShiftStatus, ShiftType } from '@/types/shifts';
import { checkMessage } from '@/lib/zodMessages';
import { typeError } from '@/lib/zodTypeError';

// Recurrence type enum matching the backend
export const RecurrenceType = {
  NONE: 'NONE',
  DAILY: 'DAILY',
  WEEKDAYS: 'WEEKDAYS',
  WEEKLY: 'WEEKLY',
  CUSTOM_WEEKDAYS: 'CUSTOM_WEEKDAYS',
} as const;
export type RecurrenceTypeValue = (typeof RecurrenceType)[keyof typeof RecurrenceType];

/**
 * The employee select value of an open shift (#2040): published without an employee,
 * claimed by employees in the app, given by the group lead's approval.
 */
export const OPEN_SHIFT_VALUE = '__open_shift__';

/** The most employees one create takes (#2121, the backend's MaxShiftEmployees). */
export const MAX_SHIFT_EMPLOYEES = 30;

/** The open-shift rules both forms share: at least one group. */
function openShiftNeedsGroup(i18n: I18n) {
  return (data: { employeeId?: string; employeeIds?: string[]; groupIds: string[] }, ctx: z.RefinementCtx) => {
    const open = data.employeeId === OPEN_SHIFT_VALUE || data.employeeIds?.[0] === OPEN_SHIFT_VALUE;
    if (open && data.groupIds.filter((id) => id && id.trim() !== '').length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['groupIds'],
        message: i18n._({
          id: 'shifts.validation.openNeedsGroup',
          message: 'An open shift needs at least one group, so the employees of that group can claim it.',
        }),
      });
    }
  };
}

// Time format regex: HH:MM (00:00 - 23:59)
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Creates the base shift form schema with i18n validation messages.
 *
 * @param i18n - Lingui i18n instance for localized error messages
 * @returns Zod schema for create shift form
 */
export const getCreateShiftSchema = (i18n: I18n) =>
  z
    .object({
      // One or several employees (#2121), or [OPEN_SHIFT_VALUE] alone for an open shift.
      employeeIds: z
        .array(z.string().min(1, checkMessage.required(i18n)))
        .min(1, {
          message: i18n._({
            id: 'shifts.validation.employeeRequired',
            message: 'Employee is required',
          }),
        })
        .max(MAX_SHIFT_EMPLOYEES, {
          message: i18n._({
            id: 'shifts.validation.employeesMax',
            message: 'At most {max} people per save',
            values: { max: MAX_SHIFT_EMPLOYEES },
          }),
        }),
      shiftDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'shifts.validation.dateRequired',
          message: 'Date is required',
        }),
          invalid: i18n._({
          id: 'shifts.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      startTime: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.startTimeFormat',
          message: 'Start time must be in HH:MM format',
        }),
      }),
      endTime: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.endTimeFormat',
          message: 'End time must be in HH:MM format',
        }),
      }),
      breakMinutes: z
        .number()
        .min(0, {
          message: i18n._({
            id: 'shifts.validation.breakMinutesMin',
            message: 'Break minutes cannot be negative',
          }),
        })
        .default(0),
      shiftType: z.enum([ShiftType.REGULAR, ShiftType.OVERTIME, ShiftType.ON_CALL], {
        error: typeError({
          required: i18n._({
          id: 'shifts.validation.shiftTypeRequired',
          message: 'Shift type is required',
        }),
        }),
      }),
      breakStart: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.breakStartFormat',
          message: 'Break start must be in HH:MM format',
        }),
      }).optional().or(z.literal('')),
      breakEnd: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.breakEndFormat',
          message: 'Break end must be in HH:MM format',
        }),
      }).optional().or(z.literal('')),
      groupIds: z.array(z.string()).default([]),
      notes: z.string().optional(),
      recurrenceType: z
        .enum([
          RecurrenceType.NONE,
          RecurrenceType.DAILY,
          RecurrenceType.WEEKDAYS,
          RecurrenceType.WEEKLY,
          RecurrenceType.CUSTOM_WEEKDAYS,
        ])
        .default(RecurrenceType.NONE),
      repetitions: z.coerce.number<number>().optional(),
      // Selected ISO weekdays (1=Mon .. 7=Sun) for CUSTOM_WEEKDAYS recurrence.
      weekdays: z.array(z.number().int(checkMessage.wholeRange(i18n, 1, 7)).min(1, checkMessage.wholeRange(i18n, 1, 7)).max(7, checkMessage.wholeRange(i18n, 1, 7))).default([]),
    })
    .refine(
      (data) => {
        // Compare times as strings (HH:MM format allows direct comparison)
        return data.endTime > data.startTime;
      },
      {
        message: i18n._({
          id: 'shifts.validation.endAfterStart',
          message: 'End time must be after start time',
        }),
        path: ['endTime'],
      }
    )
    .refine(
      (data) => {
        // Both break start and end must be provided together, or neither
        const hasStart = data.breakStart && data.breakStart !== '';
        const hasEnd = data.breakEnd && data.breakEnd !== '';
        if (hasStart !== hasEnd) return false;
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.breakTimesBothRequired',
          message: 'Both break start and end times must be provided together',
        }),
        path: ['breakEnd'],
      }
    )
    .refine(
      (data) => {
        // Break end must be after break start
        const hasStart = data.breakStart && data.breakStart !== '';
        const hasEnd = data.breakEnd && data.breakEnd !== '';
        if (hasStart && hasEnd) {
          return data.breakEnd! > data.breakStart!;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.breakEndAfterStart',
          message: 'Break end must be after break start',
        }),
        path: ['breakEnd'],
      }
    )
    .refine(
      (data) => {
        // Repetitions required and must be >= 1 for recurring shifts
        if (data.recurrenceType !== RecurrenceType.NONE) {
          return data.repetitions !== undefined && data.repetitions >= 1;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.repetitionsRequired',
          message: 'Number of repetitions is required for recurring shifts',
        }),
        path: ['repetitions'],
      }
    )
    .superRefine((data, ctx) => {
      // Max repetitions based on recurrence type
      if (data.recurrenceType !== RecurrenceType.NONE && data.repetitions) {
        const max = data.recurrenceType === RecurrenceType.WEEKLY ? 52 : 365;
        if (data.repetitions > max) {
          // Branch the message (and cap) by recurrence type so non-weekly
          // recurrences don't show a "weekly" message.
          ctx.addIssue({
            code: 'custom',
            message:
              data.recurrenceType === RecurrenceType.WEEKLY
                ? i18n._({
                    id: 'shifts.validation.repetitionsMaxWeekly',
                    message: 'Weekly repetitions cannot exceed 52 weeks',
                  })
                : i18n._({
                    id: 'shifts.validation.repetitionsMax',
                    message: 'Repetitions cannot exceed {max}',
                    values: { max: 365 },
                  }),
            path: ['repetitions'],
          });
        }
      }
    })
    .refine(
      (data) => {
        // At least one weekday must be selected for custom-weekday recurrence.
        if (data.recurrenceType === RecurrenceType.CUSTOM_WEEKDAYS) {
          return data.weekdays.length >= 1;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.weekdaysRequired',
          message: 'Select at least one weekday',
        }),
        path: ['weekdays'],
      }
    )
    .superRefine(openShiftNeedsGroup(i18n))
    .superRefine((data, ctx) => {
      // An open shift is one-off (#2040): a series needs an owner.
      if (data.employeeIds[0] === OPEN_SHIFT_VALUE && data.recurrenceType !== RecurrenceType.NONE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recurrenceType'],
          message: i18n._({
            id: 'shifts.validation.openNotRecurring',
            message: 'An open shift is a single shift. Set Recurrence to None.',
          }),
        });
      }
    });

/**
 * Creates the update shift form schema with status and optimistic lock fields.
 *
 * @param i18n - Lingui i18n instance for localized error messages
 * @returns Zod schema for update shift form
 */
export const getUpdateShiftSchema = (i18n: I18n) =>
  z
    .object({
      employeeId: z.string().min(1, {
        message: i18n._({
          id: 'shifts.validation.employeeRequired',
          message: 'Employee is required',
        }),
      }),
      shiftDate: z.date({
        error: typeError({
          required: i18n._({
          id: 'shifts.validation.dateRequired',
          message: 'Date is required',
        }),
          invalid: i18n._({
          id: 'shifts.validation.dateInvalid',
          message: 'Invalid date',
        }),
        }),
      }),
      startTime: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.startTimeFormat',
          message: 'Start time must be in HH:MM format',
        }),
      }),
      endTime: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.endTimeFormat',
          message: 'End time must be in HH:MM format',
        }),
      }),
      breakMinutes: z
        .number()
        .min(0, {
          message: i18n._({
            id: 'shifts.validation.breakMinutesMin',
            message: 'Break minutes cannot be negative',
          }),
        })
        .default(0),
      shiftType: z.enum([ShiftType.REGULAR, ShiftType.OVERTIME, ShiftType.ON_CALL], {
        error: typeError({
          required: i18n._({
          id: 'shifts.validation.shiftTypeRequired',
          message: 'Shift type is required',
        }),
        }),
      }),
      status: z.enum(
        [ShiftStatus.DRAFT, ShiftStatus.PUBLISHED, ShiftStatus.CONFIRMED, ShiftStatus.CANCELLED],
        {
          error: typeError({
            required: i18n._({
            id: 'shifts.validation.statusRequired',
            message: 'Status is required',
          }),
          }),
        }
      ),
      breakStart: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.breakStartFormat',
          message: 'Break start must be in HH:MM format',
        }),
      }).optional().or(z.literal('')),
      breakEnd: z.string().regex(TIME_REGEX, {
        message: i18n._({
          id: 'shifts.validation.breakEndFormat',
          message: 'Break end must be in HH:MM format',
        }),
      }).optional().or(z.literal('')),
      groupIds: z.array(z.string()).default([]),
      notes: z.string().optional(),
      currentUpdatedAt: z.string().min(1, {
        message: i18n._({
          id: 'shifts.validation.currentUpdatedAtRequired',
          message: 'Version timestamp is required',
        }),
      }),
    })
    .refine(
      (data) => {
        return data.endTime > data.startTime;
      },
      {
        message: i18n._({
          id: 'shifts.validation.endAfterStart',
          message: 'End time must be after start time',
        }),
        path: ['endTime'],
      }
    )
    .refine(
      (data) => {
        const hasStart = data.breakStart && data.breakStart !== '';
        const hasEnd = data.breakEnd && data.breakEnd !== '';
        if (hasStart !== hasEnd) return false;
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.breakTimesBothRequired',
          message: 'Both break start and end times must be provided together',
        }),
        path: ['breakEnd'],
      }
    )
    .refine(
      (data) => {
        const hasStart = data.breakStart && data.breakStart !== '';
        const hasEnd = data.breakEnd && data.breakEnd !== '';
        if (hasStart && hasEnd) {
          return data.breakEnd! > data.breakStart!;
        }
        return true;
      },
      {
        message: i18n._({
          id: 'shifts.validation.breakEndAfterStart',
          message: 'Break end must be after break start',
        }),
        path: ['breakEnd'],
      }
    )
    .superRefine(openShiftNeedsGroup(i18n))
    .superRefine((data, ctx) => {
      if (data.employeeId === OPEN_SHIFT_VALUE && data.status === ShiftStatus.CONFIRMED) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['status'],
          message: i18n._({
            id: 'shifts.validation.openNotConfirmable',
            message: 'An open shift cannot be confirmed: nobody is assigned yet.',
          }),
        });
      }
    });

// Type inference for form data
export type CreateShiftFormData = z.infer<ReturnType<typeof getCreateShiftSchema>>;
/**
 * The schema's INPUT shape. Keys with a `.default()` are optional going in and
 * required coming out, so forms use `useForm<CreateShiftFormInput, unknown, CreateShiftFormData>`
 * — `zodResolver`'s `Resolver<Input, …, Output>` is not assignable to
 * `Resolver<Output, …, Output>`.
 */
export type CreateShiftFormInput = z.input<ReturnType<typeof getCreateShiftSchema>>;
export type UpdateShiftFormData = z.infer<ReturnType<typeof getUpdateShiftSchema>>;
/**
 * The schema's INPUT shape. Keys with a `.default()` are optional going in and
 * required coming out, so forms use `useForm<UpdateShiftFormInput, unknown, UpdateShiftFormData>`
 * — `zodResolver`'s `Resolver<Input, …, Output>` is not assignable to
 * `Resolver<Output, …, Output>`.
 */
export type UpdateShiftFormInput = z.input<ReturnType<typeof getUpdateShiftSchema>>;

/**
 * Transforms CreateShiftFormData to API request format.
 *
 * @param data - Form data from the create shift form
 * @returns CreateShiftRequest payload for the API
 */
export function toCreateShiftRequest(data: CreateShiftFormData): CreateShiftRequest {
  // Filter out any empty string group IDs
  const validGroupIds = data.groupIds.filter((id) => id && id.trim() !== '');

  const open = data.employeeIds[0] === OPEN_SHIFT_VALUE;
  // One employee keeps the employee_id wire shape; several go as employee_ids (#2121).
  const several = !open && data.employeeIds.length > 1;
  return {
    employee_id: open || several ? undefined : data.employeeIds[0],
    employee_ids: several ? data.employeeIds : undefined,
    open_shift: open ? true : undefined,
    shift_date: format(data.shiftDate, 'yyyy-MM-dd'),
    start_time: data.startTime,
    end_time: data.endTime,
    break_minutes: data.breakMinutes,
    break_start: data.breakStart && data.breakStart !== '' ? data.breakStart : undefined,
    break_end: data.breakEnd && data.breakEnd !== '' ? data.breakEnd : undefined,
    shift_type: data.shiftType as CreateShiftRequest['shift_type'],
    group_ids: validGroupIds.length > 0 ? validGroupIds : undefined,
    notes: data.notes || undefined,
    recurrence_type:
      data.recurrenceType !== RecurrenceType.NONE
        ? (data.recurrenceType as CreateShiftRequest['recurrence_type'])
        : undefined,
    repetitions:
      data.recurrenceType !== RecurrenceType.NONE ? data.repetitions : undefined,
    weekdays:
      data.recurrenceType === RecurrenceType.CUSTOM_WEEKDAYS ? data.weekdays : undefined,
  };
}

/**
 * Transforms UpdateShiftFormData to API request format.
 *
 * @param data - Form data from the update shift form
 * @returns UpdateShiftRequest payload for the API
 */
export function toUpdateShiftRequest(data: UpdateShiftFormData): UpdateShiftRequest {
  // Filter out any empty string group IDs
  const validGroupIds = data.groupIds.filter((id) => id && id.trim() !== '');

  const open = data.employeeId === OPEN_SHIFT_VALUE;
  return {
    employee_id: open ? undefined : data.employeeId,
    open_shift: open ? true : undefined,
    shift_date: format(data.shiftDate, 'yyyy-MM-dd'),
    start_time: data.startTime,
    end_time: data.endTime,
    break_minutes: data.breakMinutes,
    break_start: data.breakStart && data.breakStart !== '' ? data.breakStart : undefined,
    break_end: data.breakEnd && data.breakEnd !== '' ? data.breakEnd : undefined,
    shift_type: data.shiftType as UpdateShiftRequest['shift_type'],
    status: data.status as UpdateShiftRequest['status'],
    group_ids: validGroupIds.length > 0 ? validGroupIds : undefined,
    notes: data.notes || undefined,
    current_updated_at: data.currentUpdatedAt,
  };
}

/**
 * Converts a Shift response to form default values for the edit form.
 *
 * @param shift - Shift response from the API
 * @returns Default values for the UpdateShiftFormData form
 */
export function shiftToFormDefaults(shift: Shift): UpdateShiftFormData {
  return {
    employeeId: shift.is_open ? OPEN_SHIFT_VALUE : shift.employee_id || '',
    shiftDate: shift.shift_date ? new Date(shift.shift_date) : new Date(),
    startTime: shift.start_time || '08:00',
    endTime: shift.end_time || '16:00',
    breakMinutes: shift.break_minutes ?? 0,
    breakStart: shift.break_start || '',
    breakEnd: shift.break_end || '',
    shiftType: (shift.shift_type as UpdateShiftFormData['shiftType']) || ShiftType.REGULAR,
    status: (shift.status as UpdateShiftFormData['status']) || ShiftStatus.DRAFT,
    groupIds: shift.groups?.map((g) => g.id || '').filter(Boolean) || [],
    notes: shift.notes || '',
    currentUpdatedAt: shift.updated_at || '',
  };
}
